var DEFAULT_FILE_SIZE: number = 5 * 1024 * 1024;
export async function uploadLargeFile(largeFileUploadSession: LargeFileUploadSession , myBlob: Blob, name: string):Promise<void>{
	var content = new File([myBlob as Blob], name);   
    let size = content.size;
    const rangeSize = getValidRangeSize(DEFAULT_FILE_SIZE);
    const fileObj: FileObject = {
        name,
        content,
        size,
    };
    var largeUpload = new OneDriveLargeFileUploadTask(fileObj, largeFileUploadSession, {
        rangeSize,
    });
    await largeUpload.upload();
}
class OneDriveLargeFileUploadTask {
    protected file: FileObject;
	protected options: LargeFileUploadTaskOptions;
	protected uploadSession: LargeFileUploadSession;
	protected nextRange: Range;

    public constructor(file: FileObject, uploadSession: LargeFileUploadSession, options: LargeFileUploadTaskOptions = {}) {
		this.file = file;
		if (options.rangeSize === undefined) {
			options.rangeSize = DEFAULT_FILE_SIZE;
		}
		this.options = options;
		this.uploadSession = uploadSession;
		this.nextRange = new Range(0, this.options.rangeSize - 1);
    }


	/**
	 * @public
	 * @async
	 * Uploads file to the server in a sequential order by slicing the file
	 * @returns The promise resolves to uploaded response
	 */
	public async upload(): Promise<any> {
		try {
			while (true) {
				const nextRange = this.getNextRange();
				if (nextRange.maxValue === -1) {
					const err = new Error("Task with which you are trying to upload is already completed, Please check for your uploaded file");
					err.name = "Invalid Session";
					throw err;
				}
				const fileSlice = this.sliceFile(nextRange);
				const response = await this.uploadSlice(fileSlice, nextRange, this.file.size);
				// Upon completion of upload process incase of onedrive, driveItem is returned, which contains id
				if (response.id !== undefined) {
					return response;
				} else {
					this.updateTaskStatus(response);
				}
			}
		} catch (err) {
			throw err;
		}
	}


   	/**
	 * @public
	 * @async
	 * Uploads given slice to the server
	 * @param {ArrayBuffer | Blob | File} fileSlice - The file slice
	 * @param {Range} range - The range value
	 * @param {number} totalSize - The total size of a complete file
	 */  
    public async uploadSlice(fileSlice:  Blob | ArrayBuffer, range: Range, totalSize: number): Promise<any> {
        try {
            /*var settings: any = {};
            settings.headers = {
                "Content-Length": `${range.maxValue - range.minValue + 1}`,
                "Content-Range": `bytes ${range.minValue}-${range.maxValue}/${totalSize}`,
            }
			settings.processData = false;*/
			var myHeaders = {
				"Content-Length":`${range.maxValue - range.minValue + 1}`,
				"Content-Range":`bytes ${range.minValue}-${range.maxValue}/${totalSize}`
			}
			var r:RequestInit = {
				headers : myHeaders,
				method: "PUT",
				body: fileSlice
			}
			var response = await fetch(this.uploadSession.url, r)
			if (response.ok){
				var res = await response.json();
				return res;
			}
			throw "Upload error - " +response.text();
			//var d = await fb.fsoMakeTheCall(this.uploadSession.url, "PUT", fileSlice, settings);
			
        } catch (err) {
            throw err;
        }
    }

	/**
	 * @public
	 * Slices the file content to the given range
	 * @param {Range} range - The range value
	 * @returns The sliced ArrayBuffer or Blob
	 */
    public sliceFile(range: Range): ArrayBuffer | Blob {
		const blob = this.file.content.slice(range.minValue, range.maxValue + 1);
		return blob;
    }

   	/**
	 * @private
	 * Updates the expiration date and the next range
	 * @param {UploadStatusResponse} response - The response of the upload status
	 * @returns Nothing
	 */
	private updateTaskStatus(response: UploadStatusResponse): void {
		this.uploadSession.expiry = new Date(response.expirationDateTime);
		this.nextRange = this.parseRange(response.nextExpectedRanges);
    }

   	/**
	 * @private
	 * Parses given range string to the Range instance
	 * @param {string[]} ranges - The ranges value
	 * @returns The range instance
	 */ 
    private parseRange(ranges: string[]): Range {
		const rangeStr = ranges[0];
		if (typeof rangeStr === "undefined" || rangeStr === "") {
			return new Range();
		}
		const firstRange = rangeStr.split("-");
		const minVal = parseInt(firstRange[0], 10);
		let maxVal = parseInt(firstRange[1], 10);
		if (Number.isNaN(maxVal)) {
			maxVal = this.file.size - 1;
		}
		return new Range(minVal, maxVal);
    }

    /**
	 * @public
	 * Gets next range that needs to be uploaded
	 * @returns The range instance
	 */
    public getNextRange(): Range {
		if (this.nextRange.minValue === -1) {
			return this.nextRange;
		}
		const minVal = this.nextRange.minValue;
		let maxValue = minVal + this.options.rangeSize - 1;
		if (maxValue >= this.file.size) {
			maxValue = this.file.size - 1;
		}
		return new Range(minVal, maxValue);
    }
}

interface FileObject {
	content: ArrayBuffer | File;
	name: string;
	size: number;
}

interface UploadStatusResponse {
	expirationDateTime: string;
	nextExpectedRanges: string[];
}

interface LargeFileUploadTaskOptions {
	rangeSize?: number;
}

export interface LargeFileUploadSession {
	url: string;
	expiry: Date;
}

class Range {
	/**
	 * @public
	 * The minimum value of the range
	 */
	public minValue: number;

	/**
	 * @public
	 * The maximum value of the range
	 */
	public maxValue: number;

	/**
	 * @public
	 * @constructor
	 * Creates a range for given min and max values
	 * @param {number} [minVal = -1] - The minimum value.
	 * @param {number} [maxVal = -1] - The maximum value.
	 * @returns An instance of a Range
	 */
	public constructor(minVal = -1, maxVal = -1) {
		this.minValue = minVal;
		this.maxValue = maxVal;
	}
}

/**
 * @constant
 * Rounds off the given value to a multiple of 320 KB
 * @param {number} value - The value
 * @returns The rounded off value
 */
const roundTo320KB = (value: number): number => {
	if (value > 320 * 1024) {
		value = Math.floor(value / (320 * 1024)) * 320 * 1024;
	}
	return value;
};

/**
 * @constant
 * Get the valid rangeSize for a file slicing (validity is based on the constrains mentioned in here
 * {@link https://developer.microsoft.com/en-us/graph/docs/api-reference/v1.0/api/driveitem_createuploadsession#upload-bytes-to-the-upload-session})
 *
 * @param {number} [rangeSize = DEFAULT_FILE_SIZE] - The rangeSize value.
 * @returns The valid rangeSize
 */
const getValidRangeSize = (rangeSize: number = DEFAULT_FILE_SIZE): number => {
	const sixtyMB = 60 * 1024 * 1024;
	if (rangeSize > sixtyMB) {
		rangeSize = sixtyMB;
	}
	return roundTo320KB(rangeSize);
};